// =============================================================================
// src/features/analytics/analytics-insights-nav-controller.ts - Comportamento
// da navegacao entre edicoes do Resumo de IA. Modulo proprio (nao entra no
// analytics-controller.ts, ja em 669 linhas): binding dos 2 botoes, loading
// com aria-busy, toast em falha e re-render parcial do host (molde
// renderAnalyticsFeedCards — innerHTML sem re-bind: o documento nao tem
// listeners proprios). Cache hit troca sem fetch; miss usa o loader registrado.
// =============================================================================

import { showMiniToast } from '../../chrome/mini-toast/index';
import { analyticsInsightActiveDocMarkup } from './analytics-digest-markup';
import { loadAnalyticsInsightEditionBody } from './analytics-insights-edition-loader';
import { parseAnalyticsInsightEditionMarkdown } from './analytics-insights-edition-parser';
import {
  analyticsInsightAdjacentEditionRef,
  cacheAnalyticsInsightParsedDoc,
  cachedAnalyticsInsightParsedDoc,
  setAnalyticsInsightActiveEditionDate,
} from './analytics-insights-live-store';
import { analyticsInsightNavLabelMarkup } from './analytics-insights-nav-markup';

let insightNavLoading = false;

/** Liga prev/next; no fallback estatico a nav nem existe no DOM (no-op). */
export function bindAnalyticsInsightEditionNav(): void {
  for (const navButton of insightNavButtons()) {
    navButton.addEventListener('click', () => {
      void navigateAnalyticsInsightEdition(navButton.dataset.analyticsInsightNav === 'prev' ? 'prev' : 'next');
    });
  }
}

async function navigateAnalyticsInsightEdition(direction: 'prev' | 'next'): Promise<void> {
  if (insightNavLoading) return;
  const targetRef = analyticsInsightAdjacentEditionRef(direction);
  if (targetRef === null) return;
  if (cachedAnalyticsInsightParsedDoc(targetRef.editionDate) !== null) {
    return activateInsightEdition(targetRef.editionDate);
  }
  setInsightNavLoading(true);
  const bodyLoad = await loadAnalyticsInsightEditionBody(targetRef);
  setInsightNavLoading(false);
  if (bodyLoad === null) {
    showMiniToast('Nao foi possivel carregar a edicao - tente de novo', 'ti-alert-triangle');
    return refreshInsightNavState();
  }
  cacheAnalyticsInsightParsedDoc(
    targetRef.editionDate,
    parseAnalyticsInsightEditionMarkdown(bodyLoad.markdownBody, bodyLoad.docKind),
  );
  activateInsightEdition(targetRef.editionDate);
}

/** Ativa a data, re-renderiza o host e atualiza rotulo/pontas da nav. */
function activateInsightEdition(editionDate: string): void {
  setAnalyticsInsightActiveEditionDate(editionDate);
  const docHost = insightDocHost();
  if (docHost !== null) docHost.innerHTML = analyticsInsightActiveDocMarkup();
  refreshInsightNavState();
}

function refreshInsightNavState(): void {
  const navLabel = document.querySelector<HTMLElement>('[data-analytics-insight-nav-label]');
  if (navLabel !== null) navLabel.innerHTML = analyticsInsightNavLabelMarkup();
  for (const navButton of insightNavButtons()) {
    const direction = navButton.dataset.analyticsInsightNav === 'prev' ? 'prev' : 'next';
    navButton.disabled = insightNavLoading || analyticsInsightAdjacentEditionRef(direction) === null;
  }
}

function setInsightNavLoading(loading: boolean): void {
  insightNavLoading = loading;
  const docHost = insightDocHost();
  if (docHost !== null) {
    docHost.classList.toggle('is-loading', loading);
    if (loading) docHost.setAttribute('aria-busy', 'true');
    else docHost.removeAttribute('aria-busy');
  }
  refreshInsightNavState();
}

function insightNavButtons(): readonly HTMLButtonElement[] {
  return [...document.querySelectorAll<HTMLButtonElement>('[data-analytics-insight-nav]')];
}

function insightDocHost(): HTMLElement | null {
  return document.getElementById('analyticsInsightDocHost');
}

export function __resetAnalyticsInsightNavForTests(): void {
  insightNavLoading = false;
}
